import { TabsListProvider, useTabsList } from '@mui/base';
import cn from 'classnames';
import { forwardRef } from 'react';

import type { ForwardedRef, ReactElement } from 'react';

import type { TabListProps } from './tab-list.types';

import './tab-list.scss';

/**
 * The container that houses the tabs.
 * Responsible for handling focus and keyboard navigation between tabs.
 *
 * ### Usages
 *
 * ```tsx
 * import { Tabs, TabList, Tab, TabPanel } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <Tabs>
 *       <TabList variant='primary'>
 *         <Tab value="tab-1">Tab 1</Tab>
 *         <Tab value="tab-2">Tab 2</Tab>
 *         <Tab value="tab-3">Tab 3</Tab>
 *       </TabList>
 *       <TabPanel value="tab-1">Tab 1 content</TabPanel>
 *       <TabPanel value="tab-2">Tab 2 content</TabPanel>
 *       <TabPanel value="tab-3">Tab 3 content</TabPanel>
 *     </Tabs>
 *   );
 * }
 * ```
 */
const TabList = forwardRef((props: TabListProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
  const {
    children,
    className,
    variant = 'primary',
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  const { contextValue, getRootProps } = useTabsList({
    rootRef: forwardedRef,
  });

  const rootProps = getRootProps(rest);

  const classes = cn(
    'rbui-tab-list',
    {
      'rbui-tab-list--secondary': variant === 'secondary',
    },
    className,
  );

  return (
    <TabsListProvider value={contextValue}>
      <div {...rootProps} className={classes}>
        {children}
      </div>
    </TabsListProvider>
  );
});

TabList.displayName = 'TabList';

export default TabList;
